import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { t } from '../i18n';
import { api } from '../lib/api';
import { usePopover } from './usePopover';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string | null;
  createdAt: string;
  readAt: string | null;
}

export const NOTIFICATIONS_KEY = ['notificaciones'] as const;
const KEY = NOTIFICATIONS_KEY;

/** Bandeja del usuario (las 15 más recientes). Se actualiza cada minuto. */
export const useNotifications = () =>
  useQuery({
    queryKey: KEY,
    queryFn: () => api<{ items: NotificationItem[]; unread: number }>('/api/notificaciones?limite=15'),
    refetchInterval: 60_000,
  });

/** Campana de la bandeja (GET /api/notificaciones). */
export function NotificationsBell({ dark = false }: { dark?: boolean }) {
  const { open, setOpen, toggle, ref } = usePopover();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useNotifications();
  const markRead = useMutation({
    mutationFn: (id: string) => api(`/api/notificaciones/${id}/leida`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const markAll = useMutation({
    mutationFn: () => api('/api/notificaciones/leer-todas', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  const unread = data?.unread ?? 0;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={`relative rounded p-2 focus:outline-none focus-visible:ring-2 ${dark ? 'text-white/80 hover:bg-white/10 focus-visible:ring-senal' : 'text-slate-600 hover:bg-slate-100 focus-visible:ring-marca/40'}`}
        aria-label={`${t('notificaciones.titulo')}${unread ? ` (${unread})` : ''}`}
        aria-expanded={open}
        onClick={toggle}
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-4 text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-80 rounded-md border border-slate-200 bg-white text-slate-800 shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <span className="text-sm font-semibold">{t('notificaciones.titulo')}</span>
            {unread > 0 && (
              <button type="button" className="text-xs text-marca hover:underline" onClick={() => markAll.mutate()}>
                {t('notificaciones.marcar_todas')}
              </button>
            )}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {(data?.items ?? []).length === 0 && <li className="px-3 py-4 text-sm text-slate-500">{t('notificaciones.vacias')}</li>}
            {(data?.items ?? []).map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={`block w-full px-3 py-2 text-left hover:bg-slate-50 ${n.readAt ? 'opacity-60' : ''}`}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate(n.id);
                    // Solo rutas internas: nunca se navega a otro sitio desde una notificación.
                    if (n.link?.startsWith('/') && !n.link.startsWith('//')) {
                      setOpen(false);
                      navigate(n.link);
                    }
                  }}
                >
                  <span className="block text-sm font-medium">{n.title}</span>
                  <span className="line-clamp-2 block text-xs text-slate-600">{n.body}</span>
                  <span className="block text-[11px] text-slate-400">{new Date(n.createdAt).toLocaleString('es-PA')}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
